<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import SearchPicker from './SearchPicker.vue'

/**
 * What a lead or opportunity is interested in: products or their modules (D-94) — those chosen as
 * chips, the rest found by a search (modules under their product), never all listed in the form.
 */
const props = defineProps({ modelValue: { type: Array, default: () => [] }, disabled: Boolean })
const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()
const meta = useMeta()
const keyOf = (i) => (i.module_uuid ? `m:${i.module_uuid}` : `p:${i.product_uuid}`)
const options = computed(() => (meta.data?.products || []).flatMap((p) => {
  const product = meta.localized(p.name)
  // Under the product's heading: the whole product first, then its modules (a chip names the product too).
  return [
    { value: `p:${p.uuid}`, label: t('common.wholeProduct'), group: product, chip: product },
    ...(p.modules || []).map((m) => ({ value: `m:${m.uuid}`, label: meta.localized(m.name), group: product })),
  ]
}))
const chosen = computed(() => props.modelValue.map(keyOf))
// Those kept keep what was noted with them; a new one is just itself.
function set(values) {
  const kept = new Map(props.modelValue.map((i) => [keyOf(i), i]))
  emit('update:modelValue', values.map((v) => kept.get(v) || (v.startsWith('m:') ? { module_uuid: v.slice(2) } : { product_uuid: v.slice(2) })))
}
</script>

<template>
  <div data-testid="product-interests">
    <SearchPicker :model-value="chosen" :options="options" :disabled="disabled" :placeholder="$t('common.productSearch')" :limit="1000" @update:model-value="set" />
    <p v-if="!options.length" class="text-sm text-faint">{{ $t('common.empty') }}</p>
  </div>
</template>
